import React, { useState, useRef, useEffect } from "react";
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { Input, TextArea } from '@CommonFunctionalDisplay/InputBox';
import { TextField, InputAdornment, Autocomplete } from '@mui/material';
import { getCountries, getStates } from 'country-state-picker';
import { User, Mail, Briefcase, FileText, Camera, ChevronRight, Map, MapPin, SkipForward, Building2, Globe, Users, Factory, ChevronDown, ChevronLeft, CheckCircle2 } from "lucide-react";
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OrgService from '@Helpers/URLHelpers/OrgService';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import './OnboardingPage.scss';

const INITIAL_DATA = {
    photo: null,
    fullName: "",
    email: "",
    designation: "",
    country: "",
    state: "",
    aboutMe: "",
    orgName: "",
    website: "",
    companySize: "",
    industry: "",
};

const Step = {
    PERSONAL: 1,
    ORGANIZATION: 2,
};

export default function OnboardingPage() {

    const [cursor, setCursor] = useState('auto');
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(Step.PERSONAL);
    const [formData, setFormData] = useState({});
    const [isCompleted, setIsCompleted] = useState(false);
    const [errors, setErrors] = useState({});
    const [states, setStates] = useState([]);
    const [photoUrl, setPhotoUrl] = useState('');
    const [country, setCountry] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [userState, setUserState] = useState('');
    const [userCountry, setUserCountry] = useState(null);
    const [orgErrors, setOrgErrors] = useState({});
    const { activeUser, activeUserId, setActiveUser } = useReduxActiveUser();
    const { setCurrentOrg } = useReduxOrg();
    const { setCurrentProject } = useReduxProject();
    const navigate = useNavigate();
    const orgService = new OrgService();
    const fileInputRef = useRef(null);
    let countries = getCountries();

    useEffect(()=>{
        setFormData(({...formData, userId: activeUserId, user: activeUser, org: { orgName: ''} }))
    },[])

    const updateFormData = (updates) => {
        const sanitizedUpdates = sanitizeObject(updates);
        if(currentStep == 1) {
            setFormData((prev) => ({ ...prev, user: {...prev.user, ...sanitizedUpdates }}));
        } else {
            setFormData((prev) => ({ ...prev, org: {...prev.org, ...sanitizedUpdates }}));
        }
    };

    const sanitizeObject = (obj) => {
        const sanitized = {};

        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            sanitized[key] = typeof value === 'string' ? DOMPurify.sanitize(value) : value;
        });
        return sanitized;
    };

    // Step 1 validation
    const validateStepOne = () => {
        const newErrors = {};
        if (!formData?.user.fullName.trim()) {
            newErrors.fullName = "Full Name is required";
        }
        if (!formData?.user.email.trim()) {
            newErrors.email = "Email address is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.user.email)) {
            newErrors.email = "Please enter a valid email address";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Step 2 validation
    const validateStepTwo = () => {
        const newErrors = {};
        if (!formData?.org?.orgName.trim()) {
            newErrors.orgName = "Organization Name is required";
        }
        setOrgErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Step 1 submit
    const handleStepOneSubmit = (e) => {
        e.preventDefault();
        if (validateStepOne()) {
            setCurrentStep(Step.ORGANIZATION);
        }
    };

    // Step 2 submit
    const handleStepTwoSubmit = async (e) => {
        setLoading(true);
        e.preventDefault();
        if (validateStepTwo()) {
            await createDefaultOrgAndProject(formData);
        }
        setLoading(false);
    };

    const createDefaultOrgAndProject = async(body) => {
        setCursor('wait');
        try {
            let response = await orgService.setupLeadsUser(body);
            if(response.status < 400) {
                response = JSON.parse(response?.body);
                setCurrentOrg(response?.org);
                setCurrentProject(response?.project);
                setActiveUser(formData?.user);
                setIsCompleted(true);
                SnackbarManager.show('Setup completed', 'success');
            } else {
                SnackbarManager.show('Something went wrong!', 'error')
            }
        } catch(err) {
            SnackbarManager.show('Something went wrong!', 'error');
        }
        setCursor('auto');
    }

    const handleFileChange = async (e) => {
        if (e.target.files && e.target.files[0]) {
            let file = e.target.files[0];
            if (validateImage(file)) {
                let base64File = await convertFileToBase64(file);
                setPhotoUrl(URL.createObjectURL(file));
                updateFormData({ photo:base64File });
            }

        }
    };

    const handleRemoveProfilePicture = () => {
        updateFormData({ photo: null });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Skip/back handlers
    const handleSkip = async () => {
        if (currentStep === Step.PERSONAL) {
            setCurrentStep(Step.ORGANIZATION);
        } else {
            await createDefaultOrgAndProject(formData);
            setIsCompleted(true);
        }
    };

    const handleBack = () => {
        if (currentStep === Step.ORGANIZATION) {
            setCurrentStep(Step.PERSONAL);
        }
    };

    const handleCountryChange = (e, value) => {
        if (value) {
            setStates(getStates(value.code));
            setCountry(value.name);
            setCountryCode(value.code);
            setUserCountry(value);
            setUserState('');
            updateFormData({ country: value.name, state: '', countryCode: value.code });
        } else {
            setStates([]);
            setCountry('');
            setCountryCode('');
            setUserCountry(null);
            setUserState('');
            updateFormData({ country: '', state: '' });
        }
    };

    const handleStateChange = (e, value) => {
        setUserState(value || '');
        updateFormData({ state: value || '' });
    };

    const validateImage = (file) => {
        if (file.type.split('/')[0] === 'image') {
            let sizeLimit = 1024 * 1024;
            if (file.size > sizeLimit) {
                SnackbarManager.show(
                    'Image size exceeded 1MB!',
                    'error'
                );
                return false;
            }
            return true;
        } else {
            SnackbarManager.show(
                'Please upload a valid image file.',
                'error'
            );
            return false;
        }
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    // Completed
    if (isCompleted) {
        return (
            <div className="gs-page gs-center">
                <div className="gs-card gs-completed">
                    <div className="gs-success-icon">
                        <CheckCircle2 size={48} />
                    </div>
                    <h2>You’re all set!</h2>
                    <p>Welcome to KaizIQ. We’ve set up your workspace.</p>
                    <button
                        className="gs-primary-btn"
                        onClick={() => navigate('/home')}
                    >
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="gs-page">
            <header className="gs-header">
                <div />
                <div className="gs-logo">
                    <h1>KaizIQ</h1>
                </div>
                <div />
            </header>
            <main className="gs-main">
                <div className="gs-progress">
                    <div className={`gs-bar ${currentStep >= 1 ? "active" : ""}`} />
                    <div className={`gs-bar ${currentStep >= 2 ? "active" : ""}`} />
                    <span>STEP {currentStep}/2</span>
                </div>
                <div className="gs-card" style={{width : currentStep !== 1 ? 'auto' : '100%'}}>
                    {currentStep === Step.PERSONAL && (
                        <form
                            onSubmit={handleStepOneSubmit}
                            noValidate
                            className="gs-stepone-form"
                        >
                            <div className="gs-stepone-header">
                                <h2>Let's start with you</h2>
                                <p>Tell us a bit about yourself to personalize your experience.</p>
                            </div>
                            <div className="gs-stepone-content">
                                    <div className="gs-stepone-avatar">
                                        <div
                                            className="gs-stepone-avatar-box"
                                            onClick={() => fileInputRef.current?.click()}
                                            style={{ cursor: 'pointer', position: 'relative' }}
                                        >
                                            {photoUrl ? (
                                                <>
                                                    <img src={photoUrl} alt="Profile" />
                                                    <button
                                                        type="button"
                                                        className="gs-stepone-remove-btn"
                                                        style={{ position: 'absolute', top: 0, right: 0, background: 'none', border: 'none', cursor: 'pointer' }}
                                                        onClick={(e) => { e.stopPropagation(); handleRemoveProfilePicture(); }}
                                                        title="Remove Photo"
                                                    >
                                                        <DeleteOutlineIcon style={{ color: '#d32f2f' }} />
                                                    </button>
                                                </>
                                            ) : (
                                                <User size={40} />
                                            )}
                                            <div className="gs-stepone-avatar-overlay">
                                                <Camera size={28} />
                                            </div>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                style={{ display: 'none' }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="gs-stepone-upload-btn"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            Upload Photo
                                        </button>
                                    </div>
                                <div className="gs-stepone-fields">
                                    <div className="gs-stepone-grid">
                                        <Input
                                            label="Full Name"
                                            placeholder="e.g. John Doe"
                                            icon={User}
                                            required
                                            value={formData?.user?.fullName}
                                            onChange={(e) => {
                                                updateFormData({ fullName: e.target.value });
                                                if (errors.fullName) {
                                                    setErrors((prev) => ({ ...prev, fullName: undefined }));
                                                }
                                            }}
                                            error={errors.fullName}
                                        />
                                        <Input
                                            label="Email Address"
                                            placeholder="john@kaiziq.io"
                                            type="email"
                                            icon={Mail}
                                            required
                                            value={formData?.user?.email}
                                            onChange={(e) => {
                                                updateFormData({ email: e.target.value });
                                                if (errors.email) {
                                                    setErrors((prev) => ({ ...prev, email: undefined }));
                                                }
                                            }}
                                            error={errors.email}
                                        />
                                    </div>
                                    <Input
                                        label="Job Title / Designation"
                                        placeholder="e.g. Marketing Manager"
                                        icon={Briefcase}
                                        value={formData?.user?.designation}
                                        onChange={(e) =>
                                            updateFormData({ designation: e.target.value })
                                        }
                                    />
                                    <div className="gs-stepone-grid">
                                        <div className="gs-step2-field" style={{ width: '100%' }}>
                                            <label className="gs-step2-label">Country</label>
                                            <div className="gs-step2-select-wrapper">
                                                <Autocomplete
                                                    options={countries}
                                                    value={userCountry}
                                                    getOptionLabel={(option) => option?.name || ''}
                                                    onChange={handleCountryChange}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select your country"
                                                            variant="outlined"
                                                            size="small"
                                                            className="gs-step2-select"
                                                            InputLabelProps={{ shrink: false }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <MapPin className="country-icon" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                    className="gs-step2-autocomplete"
                                                />
                                                <span className="gs-step2-chevron">▾</span>
                                            </div>
                                        </div>
                                        <div className="gs-step2-field" style={{ width: '100%' }}>
                                            <label className="gs-step2-label">State / Region</label>
                                            <div className="gs-step2-select-wrapper">
                                                <Autocomplete
                                                    options={states}
                                                    value={userState}
                                                    getOptionLabel={(option) => option || ''}
                                                    onChange={handleStateChange}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Select your state"
                                                            variant="outlined"
                                                            size="small"
                                                            className="gs-step2-select"
                                                            InputLabelProps={{ shrink: false }}
                                                            InputProps={{
                                                              ...params.InputProps,
                                                              startAdornment: (
                                                                <InputAdornment position="start">
                                                                  <Map className="country-icon" />
                                                                </InputAdornment>
                                                              ),
                                                            }}
                                                        />
                                                    )}
                                                    className="gs-step2-autocomplete"
                                                    disabled={!country}
                                                />
                                                <span className="gs-step2-chevron">▾</span>
                                            </div>
                                        </div>
                                    </div>
                                    <TextArea
                                        label="About Me (Optional)"
                                        placeholder="Tell us a little about your role and what you hope to achieve..."
                                        icon={FileText}
                                        value={formData?.user?.about}
                                        onChange={(e) =>
                                            updateFormData({ about: e.target.value })
                                        }
                                    />
                                </div>
                            </div>
                            <div className="gs-stepone-footer">
                                <button
                                    type="button"
                                    onClick={handleSkip}
                                    className="gs-stepone-skip"
                                >
                                    Skip <SkipForward size={14} />
                                </button>
                                <button type="submit" className="gs-stepone-next">
                                    Next Step <ChevronRight size={18} />
                                </button>
                            </div>
                        </form>
                    )}
                    {currentStep === Step.ORGANIZATION && (
                        <form
                            onSubmit={handleStepTwoSubmit}
                            noValidate
                            className="gs-step2-form"
                        >
                            <div className="gs-step2-header">
                                <h2>Organization Details</h2>
                                <p>Tell us about your company to tailor the workspace.</p>
                            </div>
                            <div className="gs-step2-body">
                                <Input
                                    label="Organization Name"
                                    placeholder="e.g. KaiziQ"
                                    icon={Building2}
                                    autoFocus
                                    value={formData?.org?.orgName}
                                    onChange={(e) => {
                                        updateFormData({ orgName: e.target.value });
                                        if (orgErrors.orgName) {
                                            setOrgErrors((prev) => ({ ...prev, orgName: undefined }));
                                        }
                                    }}
                                    error={orgErrors.orgName}
                                />
                                <Input
                                    label="Website URL"
                                    placeholder="https://app.kaiziq.io"
                                    type="url"
                                    icon={Globe}
                                    value={formData?.org?.website}
                                    onChange={(e) => updateFormData({ website: e.target.value })}
                                />
                            </div>
                            <div className="gs-step2-footer">
                                <div className="gs-step2-footer-left">
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="gs-step2-link-btn"
                                    >
                                        <ChevronLeft size={18} /> Back
                                    </button>
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={handleSkip}
                                        className="gs-step2-link-btn"
                                    >
                                        Skip <SkipForward size={14} />
                                    </button>
                                </div>
                                <button type="submit" className="gs-step2-primary-btn" disabled={loading}>
                                    Finish <ChevronRight size={18} />
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </main>
            <footer className="gs-footer">
                © {new Date().getFullYear()} KaizIQ Inc. All rights reserved.
            </footer>
        </div>
    );
};
